/****************************************************************************************************************
* ©2019 EdgeVerve Systems Limited, Bangalore, India. All Rights Reserved.
*
* The EdgeVerve proprietary software program ("Program"), is protected by copyrights laws, international treaties and
* other pending or existing intellectual property rights in India, the United States and other countries. The Program
* may contain / reference third party or open source components, the rights to which continue to remain with the
* applicable third party licensors or the open source community as the case may be and nothing here transfers the
* rights to the third party and open source components, except as expressly permitted. Any unauthorized reproduction,
* storage, transmission in any form or by any means (including without limitation to electronic, mechanical, printing,
* photocopying, recording or otherwise), or any distribution of this Program, or any portion of it, may result in
* severe civil and criminal penalties, and will be prosecuted to the maximum extent possible under the law.
*****************************************************************************************************************/
import { Injectable } from "@angular/core";
import { FormBase } from "../util/form/form-base";
import { UntypedFormControl, Validators, UntypedFormGroup } from "@angular/forms";
import { Textbox } from "../util/form/textbox";
import { Checkbox } from "../util/form/checkbox";
import { Constants } from "../shared.constants";
import { Dropdown } from "../util/form/dropdown";
import { DatePicker } from "../util/form/datepicker";
import { CustomDatePicker } from "../util/form/customdatepicker";
import moment_ from 'moment';
import { BehaviorSubject } from 'rxjs';
import { DropdownData } from '../components/dynamic-form/dynamic-form-field/dynamic-form-field.component';


@Injectable()
export class DynamicFormControlService {

    moment = moment_;
    private WIDTH_100: number = 100;
    private dropdownData: BehaviorSubject<DropdownData>
        = new BehaviorSubject<DropdownData>({ key: '', value: '', options: [] });
    dynamicDropdownOptions = this.dropdownData.asObservable();

    constructor() { }

    schemaToQuestions(schema, data): FormBase<any>[][] {
        let rows: FormBase<any>[][] = [];

        schema.forEach(row => {
            let questions: FormBase<any>[] = [];
            let width = this.WIDTH_100 / row.length;
            row.forEach(item => {
                if (!item.hasOwnProperty('condition')
                    || (item.hasOwnProperty('condition') && item.condition)) {
                    switch (item.controlType) {
                        case Constants.CONTROL_TYPE_TEXTBOX: questions.push(new Textbox({
                            key: item.key,
                            label: item.label,
                            value: data === undefined ? item.value : data[item.key],
                            order: item.order,
                            type: item.type,
                            validators: item.validators,
                            columnWidth: width,
                            controlType: item.controlType,
                            readonly: item.readonly === undefined ? false : item.readonly
                        }));
                            break;
                        case Constants.CONTROL_TYPE_CHECKBOX: questions.push(new Checkbox({
                            key: item.key,
                            label: item.label,
                            value: data === undefined ? item.value : data[item.key],
                            order: item.order,
                            columnWidth: width,
                            controlType: item.controlType,
                            readonly: item.readonly === undefined ? false : item.readonly
                        }));
                            break;
                        case Constants.CONTROL_TYPE_DROPDOWN: questions.push(new Dropdown({
                            key: item.key,
                            label: item.label,
                            options: item.options,
                            value: data === undefined ? [] : data[item.key],
                            columnWidth: width,
                            controlType: item.controlType,
                            readonly: item.readonly === undefined ? false : item.readonly,
                            validators: item.validators,
                            listenToChange: item.listenToChange
                        }));
                            break;
                        case Constants.CONTROL_TYPE_DATEPICKER:
                            let date;
                            if (data && data[item.key])
                                date = this.moment(data[item.key]).toDate();
                            questions.push(new DatePicker({
                                key: item.key,
                                label: item.label,
                                value: date,
                                columnWidth: width,
                                controlType: item.controlType,
                                readonly: item.readonly === undefined ? false : item.readonly,
                                max: item.max,
                                min: item.min,
                                validators: item.validators,
                            }));
                            break;
                        case Constants.CONTROL_TYPE_CUSTOM_DATEPICKER:
                            let customDate;
                            if (data && data[item.key])
                                customDate = this.moment(data[item.key]).toDate();
                            questions.push(new CustomDatePicker({
                                key: item.key,
                                label: item.label,
                                value: customDate,
                                columnWidth: width,
                                controlType: item.controlType,
                                readonly: item.readonly === undefined ? false : item.readonly,
                                validators: item.validators,
                            }));
                            break;
                    }
                }
            });
            rows.push(questions);
        });
        return rows;
    }

    toFormGroup(rows: FormBase<any>[][]): UntypedFormGroup {
        let group: any = {};
        rows.forEach(questions => {
            questions.forEach(question => {
                let validators = [];
                if (question.validators) {
                    if (question.validators['required'] && question.validators['required'].value)
                        validators.push(Validators.required);
                    if (question.validators['min'])
                        validators.push(Validators.min(question.validators['min']));
                    if (question.validators['max'])
                        validators.push(Validators.max(question.validators['max']));
                    if (question.validators['minlength']) {
                        if (question.validators['minlength'].value)
                            validators.push(Validators.minLength(question.validators['minlength'].value));
                        else
                            validators.push(Validators.minLength(question.validators['minlength']));
                    }
                    if (question.validators['maxlength']) {
                        if (question.validators['maxlength'].value)
                            validators.push(Validators.maxLength(question.validators['maxlength'].value));
                        else
                            validators.push(Validators.maxLength(question.validators['maxlength']));
                    }
                    if (question.validators['pattern'] && question.validators['pattern'].value)
                        validators.push(Validators.pattern(question.validators['pattern'].value));
                    if (question.validators['email'])
                        validators.push(Validators.email(question.validators['email']));
                }
                group[question.key] = question.validators ? new UntypedFormControl(question.value, validators)
                    : new UntypedFormControl(question.value);
            });
        });
        return new UntypedFormGroup(group);
    }

    updateDropdownOptions(data: DropdownData) {
        console.log("updateDropdownOptions | dropdownData: ", data);
        this.dropdownData.next(data);
    }
}

